import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';

interface AccessRequestFormProps {
  onClose: () => void;
  onSubmitted: () => void;
}

type ComplianceRegime = 'PCI' | 'SOC' | 'ISO' | 'Other' | '';

const PCI_BUSINESS_UNITS = [
  'Amazon.com',
  'APS(MENA)',
  'ASSPL',
  'APIPL',
  'Just Walk Out',
  'Audible',
  'One Medical',
  'Amazon Pay',
  'WWG/WFM',
];

interface PublicAoc {
  id: string;
  friendlyName: string;
  reportDate: string;
  docType?: string;
  businessUnit?: string;
}

interface DocumentSelection {
  reportId: string;
  accessMode: 'view' | 'download';
}

export function AccessRequestForm({ onClose, onSubmitted }: AccessRequestFormProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [regime, setRegime] = useState<ComplianceRegime>('');
  const [businessUnit, setBusinessUnit] = useState('');
  const [allAocs, setAllAocs] = useState<PublicAoc[]>([]);
  const [selections, setSelections] = useState<Map<string, DocumentSelection>>(new Map());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.listPublicAocs()
      .then((data) => setAllAocs(data.aocs))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filteredReports = allAocs.filter((aoc) => {
    if (!regime) return false;
    const docType = aoc.docType || 'PCI';
    if (docType !== regime) return false;
    if (regime === 'PCI' && businessUnit) {
      return (aoc.businessUnit || '') === businessUnit;
    }
    return true;
  });

  function setAccessMode(reportId: string, accessMode: 'view' | 'download') {
    setSelections((prev) => {
      const next = new Map(prev);
      next.set(reportId, { reportId, accessMode });
      return next;
    });
  }

  function removeSelection(reportId: string) {
    setSelections((prev) => {
      const next = new Map(prev);
      next.delete(reportId);
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (selections.size === 0) {
      setError('Please select at least one document and choose View or Download access.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const requestedDocuments = Array.from(selections.values());
      await api.reRequestAccess({
        reason: reason.trim() || undefined,
        requestedAocReportIds: requestedDocuments.map((d) => d.reportId),
        requestedDocuments,
        complianceRegime: regime || undefined,
        businessUnit: regime === 'PCI' ? businessUnit || undefined : undefined,
        requestType: 'add',
      });
      onSubmitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="access-request-title">
      <div className="modal-card modal-card-wide">
        <h2 id="access-request-title">{t('accessRequest.title', 'Request Document Access')}</h2>

        {error && <div className="alert alert-error" role="alert">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Step 1: Compliance Regime */}
          <fieldset className="policy-section">
            <legend>{t('accessRequest.regimeLabel', 'Compliance Framework')}</legend>
            <div className="regime-options">
              {(['PCI', 'SOC', 'ISO', 'Other'] as const).map((r) => (
                <label key={r} className="aoc-check-row">
                  <input
                    type="radio"
                    name="regime"
                    value={r}
                    checked={regime === r}
                    onChange={() => { setRegime(r); setBusinessUnit(''); }}
                  />
                  <span className="aoc-check-label">{r === 'Other' ? 'Other' : `${r}`}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Step 2: Business Unit (PCI only) */}
          {regime === 'PCI' && (
            <fieldset className="policy-section" style={{ marginTop: '1rem' }}>
              <legend>{t('accessRequest.businessUnitLabel', 'Business Unit')}</legend>
              <div className="regime-options">
                {PCI_BUSINESS_UNITS.map((bu) => (
                  <label key={bu} className="aoc-check-row">
                    <input
                      type="radio"
                      name="businessUnit"
                      value={bu}
                      checked={businessUnit === bu}
                      onChange={() => setBusinessUnit(bu)}
                    />
                    <span className="aoc-check-label">{bu}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {/* Step 3: Document list with View/Download per document */}
          {regime && (regime !== 'PCI' || businessUnit) && (
            <fieldset className="policy-section" style={{ marginTop: '1rem' }}>
              <legend>{t('accessRequest.documentsLabel', 'Available Documents')}</legend>
              {loading ? (
                <p className="form-hint">Loading...</p>
              ) : filteredReports.length === 0 ? (
                <p className="form-hint">
                  {t('accessRequest.noDocuments', 'No documents available for this selection.')}
                </p>
              ) : (
                <div className="document-access-list">
                  <div className="doc-list-header">
                    <span className="doc-list-name">Document</span>
                    <span className="doc-list-access">Access Level</span>
                  </div>
                  {filteredReports.map((report) => {
                    const sel = selections.get(report.id);
                    return (
                      <div key={report.id} className="doc-list-row">
                        <span className="doc-list-name">
                          {report.friendlyName || report.id}
                          {report.reportDate && (
                            <span className="doc-list-date"> ({report.reportDate})</span>
                          )}
                        </span>
                        <span className="doc-list-access">
                          <label className="access-radio">
                            <input
                              type="radio"
                              name={`access-${report.id}`}
                              value="view"
                              checked={sel?.accessMode === 'view'}
                              onChange={() => setAccessMode(report.id, 'view')}
                            />
                            View
                          </label>
                          <label className="access-radio">
                            <input
                              type="radio"
                              name={`access-${report.id}`}
                              value="download"
                              checked={sel?.accessMode === 'download'}
                              onChange={() => setAccessMode(report.id, 'download')}
                            />
                            Download
                          </label>
                          {sel && (
                            <button
                              type="button"
                              className="link-button doc-list-clear"
                              onClick={() => removeSelection(report.id)}
                            >
                              Clear
                            </button>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </fieldset>
          )}

          {/* Reason */}
          <div className="form-group" style={{ marginTop: '1rem' }}>
            <label htmlFor="access-request-reason">{t('accessRequest.reason', 'Reason for request')}</label>
            <textarea
              id="access-request-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={t('accessRequest.reasonPlaceholder', 'e.g. Need access for annual audit review')}
            />
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || selections.size === 0}
            >
              {submitting
                ? t('accessRequest.submitting', 'Submitting...')
                : t('accessRequest.submit', 'Submit Request')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
